/**
 * @lodex-pro/web-portal/routes/TankSetup — the company's own tank records (OPP PR 7, 0214).
 *
 * OPERATOR_ADMIN only, under OP_ROUTES_PUMPERS. An operator tank is the company's record of
 * one physical tank, on a catalog well or on a battery. Each hauler keeps its own record of
 * the same tank; a "link" ties a hauler's record to the company's.
 *
 *   - Proposals: the haulers' records not linked yet, grouped by well or battery. Each says
 *     whether confirming it joins an existing tank or makes a new one. Confirm some or all.
 *     A record can also be linked by hand to any of the company's tanks.
 *     A proposal that makes a NEW tank shows the name it gets (suggestedName: the hauler's tank
 *     name), which the admin may edit; a name is unique at its well, site or battery (13.8,
 *     D-22). It also shows the producer tank ID the new tank gets from the hauler's identifier,
 *     or says another tank already has it (producerIdTaken).
 *   - Your tanks (TankRegistrySections): the company's tank records, with placement, sizes,
 *     tags and each linked hauler's own sizes; add and edit tanks; batteries and their wells.
 *
 * Tank registry PR 1: a foreman reads the page (the registry and the batteries, no proposals and
 * no controls); a pumper gets no page (Home), and the sidebar shows them no entry. The API
 * refuses writes to anyone but the admin too (MANAGE_OPERATOR_TANKS); this is the UX.
 *
 * Tank registry PR 2: the admin also imports tanks from a CSV / XLSX (TankImportSection); a
 * foreman sees no import.
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import type {
  OperatorTankView,
  TankProposal,
} from '@lodex-pro/shared/validation/operator-portal-routes';
import { usePortalMe } from '../hooks/useOperatorPortal';
import {
  useConfirmProposals,
  useLinkTank,
  useOperatorTanks,
  useTankProposals,
  useUpdateOperatorTank,
} from '../hooks/useTankSetupAndRoutes';
import { PortalDataError } from '../components/PortalDataError';
import { portalErrorText } from '../components/portal-error-text';
import { portalIsAdmin, portalLacks, portalManagesRoutes } from '../nav/portal-features';
import { PremiumStub } from './PremiumStub';
import { BatteriesSection, RegistrySection } from './TankRegistrySections';
import { TankImportSection } from './TankImportSection';
import {
  BUTTON,
  CARD,
  INPUT,
  MUTED,
  PRIMARY_BUTTON,
  TAG,
  WARNING,
  capacityLabel,
  matchReasonLabel,
  placeKey,
  placeLabel,
  plural,
  sizesDiffer,
  sizesLabel,
} from './operator-tank-labels';

export function TankSetup(): React.JSX.Element {
  const me = usePortalMe();
  if (me.data === undefined) {
    return (
      <main style={{ padding: '24px' }}>
        {me.isError ? (
          <p role="alert">Could not load tank setup.</p>
        ) : (
          <p role="status">Loading…</p>
        )}
      </main>
    );
  }
  if (!portalManagesRoutes(me.data)) return <Navigate to="/portal" replace />;
  if (portalLacks(me.data, 'OP_ROUTES_PUMPERS')) return <PremiumStub feature="tank-setup" />;
  return <TankSetupBody isAdmin={portalIsAdmin(me.data)} />;
}

function TankSetupBody({ isAdmin }: { readonly isAdmin: boolean }): React.JSX.Element {
  return (
    <main style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '4px' }}>Tank setup</h1>
      <p style={{ ...MUTED, fontSize: '13px', marginTop: 0, marginBottom: '20px' }}>
        {isAdmin ? (
          <>
            Your haulers each keep their own record of your tanks. Confirm them here so every
            hauler&rsquo;s record of a tank points to one tank of yours, and keep your own record of
            each tank: its name, where it sits, its sizes and its tags.
          </>
        ) : (
          <>
            Your company&rsquo;s tanks and batteries. Only your company&rsquo;s admin can change
            them.
          </>
        )}
      </p>
      {isAdmin && <ProposalsSection />}
      <RegistrySection isAdmin={isAdmin} />
      <BatteriesSection isAdmin={isAdmin} />
      {isAdmin && <TankImportSection />}
    </main>
  );
}

function ProposalsSection(): React.JSX.Element {
  const proposals = useTankProposals();
  const tanks = useOperatorTanks();
  const tankList = tanks.data?.tanks ?? [];
  return (
    <section aria-label="Hauler tank records to confirm" style={{ marginBottom: '32px' }}>
      <h2 style={{ fontSize: '17px', marginBottom: '8px' }}>Hauler tank records to confirm</h2>
      {proposals.isLoading && <p role="status">Loading hauler tank records…</p>}
      {proposals.isError && (
        <PortalDataError error={proposals.error} fallback="Could not load hauler tank records." />
      )}
      {proposals.data !== undefined && (
        <Proposals proposals={proposals.data.proposals} tanks={tankList} />
      )}
    </section>
  );
}

function sortTanks(tanks: readonly OperatorTankView[]): OperatorTankView[] {
  return [...tanks].sort(
    (a, b) => placeLabel(a).localeCompare(placeLabel(b)) || a.name.localeCompare(b.name),
  );
}

interface ProposalGroup {
  readonly key: string;
  readonly label: string;
  readonly items: TankProposal[];
}

function groupProposals(proposals: readonly TankProposal[]): ProposalGroup[] {
  const groups = new Map<string, ProposalGroup>();
  for (const p of proposals) {
    const key = placeKey(p);
    const g = groups.get(key);
    if (g === undefined) groups.set(key, { key, label: placeLabel(p), items: [p] });
    else g.items.push(p);
  }
  return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label));
}

function confirmResultText(tanksCreated: number, linksCreated: number): string {
  return `${plural(tanksCreated, 'tank')} added, ${plural(linksCreated, 'hauler record')} linked.`;
}

function Proposals({
  proposals,
  tanks,
}: {
  readonly proposals: readonly TankProposal[];
  readonly tanks: readonly OperatorTankView[];
}): React.JSX.Element {
  const confirm = useConfirmProposals();
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(new Set());
  // #918 review MED-1 — records that match only a deactivated tank, for which the admin chose a
  // new tank instead of reactivating the old one.
  const [createNew, setCreateNew] = React.useState<ReadonlySet<string>>(new Set());
  // Tank registry PR 1 — the admin's edits of the names new tanks get, by hauler tank id.
  const [names, setNames] = React.useState<Readonly<Record<string, string>>>({});
  // 13.8 review L3 — records whose ID or tag match is at another place, ticked "same tank". An
  // unticked one is refused by the server (OPERATOR_TANK_MATCH_PLACE_DIFFERS), shown as the alert.
  const [acceptPlace, setAcceptPlace] = React.useState<ReadonlySet<string>>(new Set());
  const [result, setResult] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const tankName = new Map(tanks.map((t) => [t.id, t.name]));
  const byId = new Map(proposals.map((p) => [p.haulerTankId, p]));

  // A record that has gone (confirmed, or linked by hand) drops out of the selection.
  const live = new Set(proposals.map((p) => p.haulerTankId));
  const chosen = [...selected].filter((id) => live.has(id));

  const run = async (haulerTankIds: string[]): Promise<void> => {
    setResult(null);
    setError(null);
    try {
      const createNewFor = haulerTankIds.filter((id) => createNew.has(id));
      const acceptPlaceDiffers = haulerTankIds.filter((id) => acceptPlace.has(id));
      // Only the names the admin changed; the rest get their suggested name on the server.
      const changed: Record<string, string> = {};
      for (const id of haulerTankIds) {
        const suggested = byId.get(id)?.suggestedName ?? null;
        const edited = names[id];
        if (suggested === null || edited === undefined) continue;
        const name = edited.trim();
        if (name === '') {
          setError('Give each new tank a name.');
          return;
        }
        if (name !== suggested) changed[id] = name;
      }
      const r = await confirm.mutateAsync({
        haulerTankIds,
        ...(createNewFor.length > 0 ? { createNewFor } : {}),
        ...(Object.keys(changed).length > 0 ? { names: changed } : {}),
        ...(acceptPlaceDiffers.length > 0 ? { acceptPlaceDiffers } : {}),
      });
      setResult(confirmResultText(r.tanksCreated, r.linksCreated));
      setSelected(new Set());
    } catch (err) {
      setError(portalErrorText(err, 'Could not confirm those records. Try again.'));
    }
  };

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <>
      {proposals.length === 0 ? (
        <p role="status" style={MUTED}>
          Every hauler tank record is linked to one of your tanks. Nothing to confirm.
        </p>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {groupProposals(proposals).map((g) => (
              <article key={g.key} aria-label={g.label} style={CARD}>
                <h3 style={{ fontSize: '15px', margin: '0 0 8px' }}>{g.label}</h3>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {g.items.map((p) => (
                    <ProposalRow
                      key={p.haulerTankId}
                      proposal={p}
                      tanks={tanks}
                      checked={selected.has(p.haulerTankId)}
                      onToggle={(): void => {
                        toggle(p.haulerTankId);
                      }}
                      joinsName={
                        p.matchOperatorTankId === null
                          ? null
                          : (p.matchName ??
                            tankName.get(p.matchOperatorTankId) ??
                            'an existing tank')
                      }
                      deactivatedMatch={
                        p.matchOperatorTankId === null && p.inactiveMatchOperatorTankId !== null
                          ? (tanks.find((x) => x.id === p.inactiveMatchOperatorTankId) ?? null)
                          : null
                      }
                      newName={names[p.haulerTankId] ?? p.suggestedName}
                      onNewName={(name): void => {
                        setNames((prev) => ({ ...prev, [p.haulerTankId]: name }));
                      }}
                      createNew={createNew.has(p.haulerTankId)}
                      onCreateNew={(on): void => {
                        setCreateNew((prev) => {
                          const next = new Set(prev);
                          if (on) next.add(p.haulerTankId);
                          else next.delete(p.haulerTankId);
                          return next;
                        });
                      }}
                      samePlaceAccepted={acceptPlace.has(p.haulerTankId)}
                      onSamePlaceAccepted={(on): void => {
                        setAcceptPlace((prev) => {
                          const next = new Set(prev);
                          if (on) next.add(p.haulerTankId);
                          else next.delete(p.haulerTankId);
                          return next;
                        });
                      }}
                    />
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', alignItems: 'center' }}>
            <button
              type="button"
              style={PRIMARY_BUTTON}
              disabled={chosen.length === 0 || confirm.isPending}
              onClick={(): void => {
                void run(chosen);
              }}
            >
              Confirm selected{chosen.length > 0 ? ` (${chosen.length.toString()})` : ''}
            </button>
            <button
              type="button"
              style={BUTTON}
              disabled={confirm.isPending}
              onClick={(): void => {
                void run(proposals.map((p) => p.haulerTankId));
              }}
            >
              Confirm all
            </button>
          </div>
        </>
      )}
      {result !== null && (
        <p role="status" style={{ marginTop: '10px', color: 'var(--color-success)' }}>
          {result}
        </p>
      )}
      {error !== null && (
        <p role="alert" style={{ marginTop: '10px', color: 'var(--color-danger)' }}>
          {error}
        </p>
      )}
    </>
  );
}

function ProposalRow({
  proposal: p,
  tanks,
  checked,
  onToggle,
  joinsName,
  deactivatedMatch,
  newName,
  onNewName,
  createNew,
  onCreateNew,
  samePlaceAccepted,
  onSamePlaceAccepted,
}: {
  readonly proposal: TankProposal;
  readonly tanks: readonly OperatorTankView[];
  readonly checked: boolean;
  readonly onToggle: () => void;
  readonly joinsName: string | null;
  /** #918 MED-1 — the deactivated tank this record matches (no active one does), if any. */
  readonly deactivatedMatch: OperatorTankView | null;
  /** The name a NEW tank gets (the suggestion, or the admin's edit); null = joins a tank. */
  readonly newName: string | null;
  readonly onNewName: (name: string) => void;
  readonly createNew: boolean;
  readonly onCreateNew: (on: boolean) => void;
  /** 13.8 review L3 — "same tank": link a match at another place anyway. */
  readonly samePlaceAccepted: boolean;
  readonly onSamePlaceAccepted: (on: boolean) => void;
}): React.JSX.Element {
  const [linking, setLinking] = React.useState(false);
  const update = useUpdateOperatorTank();
  const [reactivateError, setReactivateError] = React.useState<string | null>(null);
  const reactivate = async (t: OperatorTankView): Promise<void> => {
    setReactivateError(null);
    try {
      await update.mutateAsync({
        id: t.id,
        name: t.name,
        capacityBbl: t.capacityBbl === null ? null : Number(t.capacityBbl),
        isActive: true,
      });
    } catch (err) {
      setReactivateError(portalErrorText(err, 'Could not reactivate that tank. Try again.'));
    }
  };
  const tag =
    deactivatedMatch === null
      ? joinsName === null
        ? 'new tank'
        : `joins ${joinsName}`
      : createNew
        ? 'new tank (the deactivated one stays off)'
        : `matches a deactivated tank: ${deactivatedMatch.name}`;
  return (
    <li style={{ padding: '6px 0', borderTop: '1px solid var(--color-border-primary)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          aria-label={`Select ${p.haulerName}'s ${p.tankName}`}
        />
        <span style={{ minWidth: '160px' }}>{p.haulerName}</span>
        <span style={{ minWidth: '140px' }}>{p.tankName}</span>
        <span style={{ ...MUTED, fontSize: '13px', minWidth: '120px' }}>
          {capacityLabel(p.capacityBbl)}
        </span>
        <span style={TAG}>{tag}</span>
        {newName !== null && p.suggestedProducerTankId != null && (
          <span style={{ ...MUTED, fontSize: '13px' }} data-testid="proposal-producer-tank-id">
            Producer tank ID {p.suggestedProducerTankId}
          </span>
        )}
        {newName !== null && (
          <label style={{ ...MUTED, fontSize: '13px', display: 'flex', gap: '6px' }}>
            named
            <input
              aria-label={`Name for the new tank from ${p.haulerName}'s ${p.tankName}`}
              value={newName}
              maxLength={120}
              onChange={(e): void => {
                onNewName(e.target.value);
              }}
              style={{ ...INPUT, width: '180px' }}
            />
          </label>
        )}
        {!linking && tanks.length > 0 && (
          <button
            type="button"
            style={{ ...BUTTON, marginLeft: 'auto' }}
            onClick={(): void => {
              setLinking(true);
            }}
          >
            Link to an existing tank…
          </button>
        )}
      </div>
      {newName !== null && p.producerIdTaken === true && (
        <p
          role="note"
          style={{ ...WARNING, borderRadius: '6px', padding: '4px 10px', margin: '6px 0 0 28px' }}
        >
          Producer tank ID {p.producerIdentifier ?? ''} is already on another tank; this tank gets
          none
        </p>
      )}
      <MatchDetails
        proposal={p}
        samePlaceAccepted={samePlaceAccepted}
        onSamePlaceAccepted={onSamePlaceAccepted}
      />
      {deactivatedMatch !== null && (
        <div
          style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            marginTop: '6px',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ ...MUTED, fontSize: '13px' }}>
            Reactivate it, or create a new tank for this record.
          </span>
          <button
            type="button"
            style={BUTTON}
            disabled={update.isPending}
            onClick={(): void => {
              void reactivate(deactivatedMatch);
            }}
          >
            Reactivate {deactivatedMatch.name}
          </button>
          <button
            type="button"
            style={BUTTON}
            aria-pressed={createNew}
            onClick={(): void => {
              onCreateNew(!createNew);
            }}
          >
            {createNew ? 'Keep it undecided' : 'Create a new tank instead'}
          </button>
          {reactivateError !== null && (
            <span role="alert" style={{ color: 'var(--color-danger)', fontSize: '13px' }}>
              {reactivateError}
            </span>
          )}
        </div>
      )}
      {linking && (
        <LinkByHand
          proposal={p}
          tanks={tanks}
          onDone={(): void => {
            setLinking(false);
          }}
        />
      )}
    </li>
  );
}

/**
 * Tank registry PR 3 (0249): how a record matched one of the company's tanks (by ID, tag or
 * name), the two tanks' sizes side by side, and a warning when an ID or tag match sits at a
 * different well or site than the hauler's record, with a "same tank" box: confirm links such a
 * match only when it is ticked (13.8 review L3). Nothing for a record that matched nothing.
 */
function MatchDetails({
  proposal: p,
  samePlaceAccepted,
  onSamePlaceAccepted,
}: {
  readonly proposal: TankProposal;
  readonly samePlaceAccepted: boolean;
  readonly onSamePlaceAccepted: (on: boolean) => void;
}): React.JSX.Element | null {
  const how = matchReasonLabel(p.matchReason, p.producerIdentifier);
  if (how === null) return null;
  const capDiffers = sizesDiffer(p.capacityBbl, p.matchCapacityBbl);
  const bpiDiffers = sizesDiffer(p.bblPerInch, p.matchBblPerInch);
  const matched = p.matchName ?? 'the matched tank';
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        margin: '6px 0 0 28px',
        fontSize: '13px',
      }}
    >
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={TAG}>{how}</span>
        <span style={MUTED} aria-label={`${p.haulerName}'s sizes`}>
          their sizes: {sizesLabel(p.capacityBbl, p.bblPerInch)}
        </span>
        <span style={MUTED} aria-label={`${matched}'s sizes`}>
          {matched}: {sizesLabel(p.matchCapacityBbl, p.matchBblPerInch)}
        </span>
        {(capDiffers || bpiDiffers) && (
          <span
            style={WARNING}
            title={`${p.haulerName}'s ${[
              capDiffers ? 'capacity' : null,
              bpiDiffers ? 'bbl per inch' : null,
            ]
              .filter((x) => x !== null)
              .join(' and ')} differ from ${matched}'s. Neither overwrites the other.`}
          >
            differs:{' '}
            {[capDiffers ? 'capacity' : null, bpiDiffers ? 'BBL/in' : null]
              .filter((x) => x !== null)
              .join(', ')}
          </span>
        )}
      </div>
      {p.matchPlaceDiffers && (
        <>
          <p
            role="note"
            style={{ ...WARNING, borderRadius: '12px', padding: '4px 10px', margin: 0 }}
          >
            Different location: this tank is at {p.matchPlaceLabel ?? 'another place'}, the
            hauler&rsquo;s is at {placeLabel(p)}. Check the ID before confirming: if it is the same
            tank, tick the box below; if not, untick the record and link it by hand.
          </p>
          <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={samePlaceAccepted}
              onChange={(e): void => {
                onSamePlaceAccepted(e.target.checked);
              }}
              aria-label={`Same tank, link ${p.haulerName}'s ${p.tankName} anyway`}
            />
            Same tank, link it anyway
          </label>
        </>
      )}
    </div>
  );
}

function LinkByHand({
  proposal: p,
  tanks,
  onDone,
}: {
  readonly proposal: TankProposal;
  readonly tanks: readonly OperatorTankView[];
  readonly onDone: () => void;
}): React.JSX.Element {
  const link = useLinkTank();
  // Tanks at the same place first, then the rest.
  const here = placeKey(p);
  const options = sortTanks(tanks).sort(
    (a, b) => Number(placeKey(b) === here) - Number(placeKey(a) === here),
  );
  const [target, setTarget] = React.useState<string>(p.matchOperatorTankId ?? '');
  const [error, setError] = React.useState<string | null>(null);

  const save = async (): Promise<void> => {
    setError(null);
    try {
      await link.mutateAsync({ operatorTankId: target, haulerTankId: p.haulerTankId });
      onDone();
    } catch (err) {
      setError(portalErrorText(err, 'Could not link that record. Try again.'));
    }
  };

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', margin: '8px 0 4px 28px' }}>
      <select
        aria-label={`Tank to link ${p.haulerName}'s ${p.tankName} to`}
        value={target}
        onChange={(e): void => {
          setTarget(e.target.value);
        }}
        style={INPUT}
      >
        <option value="">Choose one of your tanks…</option>
        {options.map((t) => (
          <option key={t.id} value={t.id}>
            {placeLabel(t)} — {t.name}
            {t.isActive ? '' : ' (inactive)'}
          </option>
        ))}
      </select>
      <button
        type="button"
        style={PRIMARY_BUTTON}
        disabled={target === '' || link.isPending}
        onClick={(): void => {
          void save();
        }}
      >
        Link
      </button>
      <button type="button" style={BUTTON} onClick={onDone}>
        Cancel
      </button>
      {error !== null && (
        <span role="alert" style={{ color: 'var(--color-danger)', fontSize: '13px' }}>
          {error}
        </span>
      )}
    </div>
  );
}
